import React, { useEffect, useState } from 'react';
import { Typography, Form, Input, Button, message, Select, Segmented } from 'antd';
import {
  BankOutlined,
  CheckCircleOutlined,
  LockOutlined,
  MailOutlined,
  MedicineBoxOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  UserOutlined,
  WhatsAppOutlined
} from '@ant-design/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { initializeApp, getApps } from 'firebase/app';
import { createUserWithEmailAndPassword, getAuth, signOut } from 'firebase/auth';
import axios from 'axios';
import { auth, db } from '../firebaseConfig';
import API from '../config/api';
import { useLanguage } from './LanguageContext';
import { buildDefaultVisitTypeConfiguration } from '../utils/defaultVisitType';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import '../styles/Login.css';

const { Title, Text } = Typography;
const CONTACT_PHONE = '+201063030622';
const CONTACT_TEL_URL = `tel:${CONTACT_PHONE}`;
const CONTACT_WHATSAPP_URL = 'https://wa.me/201063030622';

const registerCopy = {
  en: {
    brand: 'Dr Waiting',
    eyebrow: 'Start beautifully',
    headline: 'Launch a clinic workspace that feels ready from day one.',
    subtitle: 'Create the clinic, owner doctor, and assistant access in one guided flow built for busy medical teams.',
    title: 'Create your clinic',
    description: 'Register the owner and assistant accounts.',
    clinicSection: 'Clinic',
    clinicName: 'Clinic Name',
    clinicNameRequired: 'Please enter clinic name.',
    clinicNamePlaceholder: 'Clinic name',
    ownerSection: 'Owner Account',
    ownerName: 'Owner Name',
    ownerNameRequired: 'Please enter owner name.',
    ownerNamePlaceholder: 'Owner full name',
    specialty: 'Doctor Specialization',
    specialtyRequired: 'Please select doctor specialization.',
    specialtyPlaceholder: '-- Select Specialty --',
    ownerEmail: 'Owner Email',
    ownerEmailRequired: 'Please enter owner email.',
    validOwnerEmail: 'Please enter a valid owner email.',
    ownerPassword: 'Owner Password',
    ownerPasswordRequired: 'Please enter owner password.',
    ownerPasswordPlaceholder: 'Owner password',
    confirmOwnerPassword: 'Confirm Owner Password',
    confirmOwnerPasswordRequired: 'Please confirm owner password.',
    ownerPasswordsMismatch: 'Owner passwords do not match.',
    assistantSection: 'Assistant Account',
    assistantName: 'Assistant Name',
    assistantNameRequired: 'Please enter assistant name.',
    assistantNamePlaceholder: 'Assistant full name',
    assistantEmail: 'Assistant Email',
    assistantEmailRequired: 'Please enter assistant email.',
    validAssistantEmail: 'Please enter a valid assistant email.',
    assistantPassword: 'Assistant Password',
    assistantPasswordRequired: 'Please enter assistant password.',
    assistantPasswordPlaceholder: 'Assistant password',
    confirmAssistantPassword: 'Confirm Assistant Password',
    confirmAssistantPasswordRequired: 'Please confirm assistant password.',
    assistantPasswordsMismatch: 'Assistant passwords do not match.',
    passwordMin: 'Password must be at least 6 characters.',
    submit: 'Register Clinic',
    alreadyAccount: 'Already have an account?',
    login: 'Login',
    contact: 'Contact us',
    call: 'Call',
    whatsapp: 'WhatsApp',
    note: 'After registration, sign in with the owner account to open the dashboard.',
    specialtiesFailed: 'Failed to load specialties list.',
    duplicateEmails: 'Owner and assistant email must be different.',
    registered: 'Clinic registered successfully. Please login with owner account.',
    emailInUse: 'One of these emails is already registered.',
    weakPassword: 'Password must be at least 6 characters.',
    failed: 'Registration failed.'
  },
  ar: {
    brand: 'Dr Waiting',
    eyebrow: 'ابدأ بشكل أجمل',
    headline: 'أنشئ مساحة عيادة جاهزة من أول يوم.',
    subtitle: 'سجل العيادة وحساب الطبيب المالك وحساب المساعد من شاشة واحدة مصممة لفرق طبية مشغولة.',
    title: 'إنشاء العيادة',
    description: 'سجل حساب الطبيب المالك والمساعد.',
    clinicSection: 'العيادة',
    clinicName: 'اسم العيادة',
    clinicNameRequired: 'برجاء إدخال اسم العيادة',
    clinicNamePlaceholder: 'اسم العيادة',
    ownerSection: 'حساب الطبيب المالك',
    ownerName: 'اسم الطبيب المالك',
    ownerNameRequired: 'برجاء إدخال اسم الطبيب المالك',
    ownerNamePlaceholder: 'الاسم بالكامل',
    specialty: 'تخصص الطبيب',
    specialtyRequired: 'برجاء اختيار تخصص الطبيب',
    specialtyPlaceholder: '-- اختر التخصص --',
    ownerEmail: 'بريد الطبيب المالك',
    ownerEmailRequired: 'برجاء إدخال بريد الطبيب المالك',
    validOwnerEmail: 'برجاء إدخال بريد صحيح',
    ownerPassword: 'كلمة مرور الطبيب المالك',
    ownerPasswordRequired: 'برجاء إدخال كلمة المرور',
    ownerPasswordPlaceholder: 'كلمة المرور',
    confirmOwnerPassword: 'تأكيد كلمة مرور المالك',
    confirmOwnerPasswordRequired: 'برجاء تأكيد كلمة المرور',
    ownerPasswordsMismatch: 'كلمتا مرور المالك غير متطابقتين',
    assistantSection: 'حساب المساعد',
    assistantName: 'اسم المساعد',
    assistantNameRequired: 'برجاء إدخال اسم المساعد',
    assistantNamePlaceholder: 'اسم المساعد بالكامل',
    assistantEmail: 'بريد المساعد',
    assistantEmailRequired: 'برجاء إدخال بريد المساعد',
    validAssistantEmail: 'برجاء إدخال بريد صحيح للمساعد',
    assistantPassword: 'كلمة مرور المساعد',
    assistantPasswordRequired: 'برجاء إدخال كلمة مرور المساعد',
    assistantPasswordPlaceholder: 'كلمة المرور',
    confirmAssistantPassword: 'تأكيد كلمة مرور المساعد',
    confirmAssistantPasswordRequired: 'برجاء تأكيد كلمة مرور المساعد',
    assistantPasswordsMismatch: 'كلمتا مرور المساعد غير متطابقتين',
    passwordMin: 'كلمة المرور يجب ألا تقل عن 6 أحرف',
    submit: 'تسجيل العيادة',
    alreadyAccount: 'لديك حساب بالفعل؟',
    login: 'تسجيل الدخول',
    contact: 'تواصل معنا',
    call: 'اتصال',
    whatsapp: 'واتساب',
    note: 'بعد التسجيل، سجل الدخول بحساب الطبيب المالك لفتح لوحة التحكم.',
    specialtiesFailed: 'فشل تحميل قائمة التخصصات.',
    duplicateEmails: 'يجب أن يكون بريد المالك مختلفا عن بريد المساعد.',
    registered: 'تم تسجيل العيادة بنجاح. برجاء تسجيل الدخول بحساب المالك.',
    emailInUse: 'أحد هذه الحسابات مسجل من قبل.',
    weakPassword: 'كلمة المرور يجب ألا تقل عن 6 أحرف.',
    failed: 'فشل تسجيل العيادة.'
  }
};

interface RegisterFormValues {
  clinicName: string;
  ownerName: string;
  ownerSpecialtyId: string;
  ownerEmail: string;
  ownerPassword: string;
  ownerConfirmPassword: string;
  assistantName: string;
  assistantEmail: string;
  assistantPassword: string;
  assistantConfirmPassword: string;
}

interface SpecialtyOption {
  id: string;
  name_ar?: string;
  name_en?: string;
  isActive?: boolean;
}

const SECONDARY_APP_NAME = 'saas-register-create';

const getSecondaryApp = () => {
  const existing = getApps().find((app) => app.name === SECONDARY_APP_NAME);
  if (existing) return existing;

  const primaryApp = getApps()[0];
  if (!primaryApp) {
    throw new Error('Firebase app is not initialized.');
  }
  return initializeApp(primaryApp.options, SECONDARY_APP_NAME);
};

const RegisterPage: React.FC = () => {
  const [form] = Form.useForm<RegisterFormValues>();
  const [loading, setLoading] = useState(false);
  const [specialties, setSpecialties] = useState<SpecialtyOption[]>([]);
  const [specialtiesLoading, setSpecialtiesLoading] = useState(false);
  const { language, setLanguage, isRTL } = useLanguage();
  const copy = registerCopy[language];
  const navigate = useNavigate();

  useEffect(() => {
    const loadSpecialties = async () => {
      setSpecialtiesLoading(true);
      try {
        const specialtiesRef = collection(db, 'specialties');
        const specialtiesQuery = query(specialtiesRef, where('isActive', '==', true));
        const specialtiesSnapshot = await getDocs(specialtiesQuery);
        const data = specialtiesSnapshot.docs.map((item) => ({
          id: item.id,
          ...item.data()
        })) as SpecialtyOption[];

        data.sort((a, b) => (a.name_ar || '').localeCompare(b.name_ar || '', 'ar'));
        setSpecialties(data);
      } catch (error) {
        console.error('Failed to load specialties:', error);
        message.error(copy.specialtiesFailed);
      } finally {
        setSpecialtiesLoading(false);
      }
    };

    loadSpecialties();
  }, [copy.specialtiesFailed]);

  const onFinish = async (values: RegisterFormValues) => {
    const ownerEmail = values.ownerEmail.trim().toLowerCase();
    const assistantEmail = values.assistantEmail.trim().toLowerCase();

    if (ownerEmail === assistantEmail) {
      message.error(copy.duplicateEmails);
      return;
    }

    setLoading(true);
    try {
      const secondaryApp = getSecondaryApp();
      const secondaryAuth = getAuth(secondaryApp);

      const clinicDoc = {
        clinic_name_ar: values.clinicName.trim(),
        clinic_name_en: values.clinicName.trim(),
        location_ar: '',
        location_en: '',
        CurrentOrder: 0,
        achievements: '',
        appointmentLink: null,
        availableDays: [],
        availableHours: '',
        bio: '',
        centerType: 'Clinic',
        parent_clinic_id: null,
        sharedPatientFile: false
      };

      const clinicRef = await addDoc(collection(db, 'clinics'), clinicDoc);
      const clinicId = clinicRef.id;
      await updateDoc(clinicRef, { clinic_id: clinicId });

      const ownerUserCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        ownerEmail,
        values.ownerPassword
      );
      const ownerUid = ownerUserCredential.user.uid;
      const selectedSpecialty = specialties.find((item) => item.id === values.ownerSpecialtyId);
      const ownerSpecialization = selectedSpecialty
        ? (selectedSpecialty.name_ar || selectedSpecialty.name_en || '').trim()
        : '';

      const ownerDoctorDoc = {
        name: values.ownerName.trim(),
        specialty_id: values.ownerSpecialtyId,
        specialization: ownerSpecialization,
        email: ownerEmail,
        is_owner: true,
        arrived: false,
        auth_uid: ownerUid,
        disabled: false
      };

      const ownerDoctorRef = await addDoc(collection(db, 'doctors'), ownerDoctorDoc);
      const ownerDoctorId = ownerDoctorRef.id;
      await updateDoc(ownerDoctorRef, { doctor_id: ownerDoctorId });

      // Seed the same editable visit-type configuration used by Internal Services.
      // A stable type_id keeps retries idempotent for this newly created doctor.
      await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(ownerDoctorId)}`,
        buildDefaultVisitTypeConfiguration(clinicId)
      );

      const assistantUserCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        assistantEmail,
        values.assistantPassword
      );
      const assistantUid = assistantUserCredential.user.uid;

      await setDoc(doc(db, 'assistants', assistantUid), {
        email: assistantEmail,
        name: values.assistantName.trim()
      });

      await addDoc(collection(db, 'doctor_clinic_assistant'), {
        assistant_id: ownerUid,
        doctor_id: ownerDoctorId,
        clinic_id: clinicId
      });

      await addDoc(collection(db, 'doctor_clinic_assistant'), {
        assistant_id: assistantUid,
        doctor_id: ownerDoctorId,
        clinic_id: clinicId
      });

      await updateDoc(doc(db, 'clinics', clinicId), {
        clinic_id: clinicId,
      });

      await signOut(secondaryAuth);
      await signOut(auth).catch(() => {});

      message.success(copy.registered);
      form.resetFields();
      navigate('/login', {
        replace: true,
        state: { registeredEmail: ownerEmail }
      });
    } catch (error: any) {
      console.error('Registration error:', error);
      if (error.code === 'auth/email-already-in-use') {
        message.error(copy.emailInUse);
      } else if (error.code === 'auth/weak-password') {
        message.error(copy.weakPassword);
      } else {
        message.error(error.message || copy.failed);
      }
    } finally {
      setLoading(false);
    }
  };

  const specialtyOptions = specialties.map((item) => ({
    value: item.id,
    label: `${item.name_ar || ''}${item.name_en ? ` - ${item.name_en}` : ''}`.trim()
  }));

  return (
    <main className="auth-page">
      <div className="auth-shell auth-shell--register">
        <section className="auth-showcase" aria-label={copy.brand}>
          <div className="auth-brand">
            <span className="auth-brand-mark">
              <img src="/dr-waiting-logo.webp" alt="Dr Waiting logo" />
            </span>
            <span className="auth-brand-name">{copy.brand}</span>
          </div>
          <div className="auth-eyebrow">
            <SafetyCertificateOutlined />
            <span>{copy.eyebrow}</span>
          </div>
          <Title level={1}>{copy.headline}</Title>
          <p>{copy.subtitle}</p>
          <div className="auth-register-note">
            <CheckCircleOutlined />
            <span>{copy.note}</span>
          </div>
        </section>

        <section className="auth-card auth-register-card" aria-label={copy.title}>
          <div className="auth-card-inner">
            <div className="auth-card-header">
              <div className="auth-card-title">
                <Title level={2}>{copy.title}</Title>
                <Text>{copy.description}</Text>
              </div>
              <div className="auth-language-toggle">
                <Segmented
                  block
                  value={language}
                  onChange={(value) => setLanguage(value as 'en' | 'ar')}
                  options={[
                    { label: 'عربي', value: 'ar' },
                    { label: 'EN', value: 'en' }
                  ]}
                />
              </div>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              className="auth-form"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <div className="auth-section">
                <div className="auth-section-heading">
                  <span className="auth-section-icon"><BankOutlined /></span>
                  <Title level={5} className="auth-section-title">{copy.clinicSection}</Title>
                </div>
                <Form.Item
                  name="clinicName"
                  label={copy.clinicName}
                  rules={[{ required: true, message: copy.clinicNameRequired }]}
                >
                  <Input size="large" placeholder={copy.clinicNamePlaceholder} prefix={<BankOutlined />} />
                </Form.Item>
              </div>

              <div className="auth-section">
                <div className="auth-section-heading">
                  <span className="auth-section-icon"><MedicineBoxOutlined /></span>
                  <Title level={5} className="auth-section-title">{copy.ownerSection}</Title>
                </div>
                <div className="auth-form-row">
                  <Form.Item
                    name="ownerName"
                    label={copy.ownerName}
                    rules={[{ required: true, message: copy.ownerNameRequired }]}
                  >
                    <Input size="large" placeholder={copy.ownerNamePlaceholder} prefix={<UserOutlined />} />
                  </Form.Item>

                  <Form.Item
                    name="ownerSpecialtyId"
                    label={copy.specialty}
                    rules={[{ required: true, message: copy.specialtyRequired }]}
                  >
                    <Select
                      size="large"
                      placeholder={copy.specialtyPlaceholder}
                      loading={specialtiesLoading}
                      options={specialtyOptions}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  name="ownerEmail"
                  label={copy.ownerEmail}
                  rules={[
                    { required: true, message: copy.ownerEmailRequired },
                    { type: 'email', message: copy.validOwnerEmail }
                  ]}
                >
                  <Input size="large" placeholder="owner@example.com" prefix={<MailOutlined />} />
                </Form.Item>

                <div className="auth-form-row">
                  <Form.Item
                    name="ownerPassword"
                    label={copy.ownerPassword}
                    rules={[
                      { required: true, message: copy.ownerPasswordRequired },
                      { min: 6, message: copy.passwordMin }
                    ]}
                  >
                    <Input.Password size="large" placeholder={copy.ownerPasswordPlaceholder} prefix={<LockOutlined />} />
                  </Form.Item>

                  <Form.Item
                    name="ownerConfirmPassword"
                    label={copy.confirmOwnerPassword}
                    dependencies={['ownerPassword']}
                    rules={[
                      { required: true, message: copy.confirmOwnerPasswordRequired },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('ownerPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(copy.ownerPasswordsMismatch));
                        }
                      })
                    ]}
                  >
                    <Input.Password size="large" placeholder={copy.confirmOwnerPassword} prefix={<LockOutlined />} />
                  </Form.Item>
                </div>
              </div>

              <div className="auth-section">
                <div className="auth-section-heading">
                  <span className="auth-section-icon"><TeamOutlined /></span>
                  <Title level={5} className="auth-section-title">{copy.assistantSection}</Title>
                </div>
                <Form.Item
                  name="assistantName"
                  label={copy.assistantName}
                  rules={[{ required: true, message: copy.assistantNameRequired }]}
                >
                  <Input size="large" placeholder={copy.assistantNamePlaceholder} prefix={<UserOutlined />} />
                </Form.Item>

                <Form.Item
                  name="assistantEmail"
                  label={copy.assistantEmail}
                  rules={[
                    { required: true, message: copy.assistantEmailRequired },
                    { type: 'email', message: copy.validAssistantEmail }
                  ]}
                >
                  <Input size="large" placeholder="assistant@example.com" prefix={<MailOutlined />} />
                </Form.Item>

                <div className="auth-form-row">
                  <Form.Item
                    name="assistantPassword"
                    label={copy.assistantPassword}
                    rules={[
                      { required: true, message: copy.assistantPasswordRequired },
                      { min: 6, message: copy.passwordMin }
                    ]}
                  >
                    <Input.Password size="large" placeholder={copy.assistantPasswordPlaceholder} prefix={<LockOutlined />} />
                  </Form.Item>

                  <Form.Item
                    name="assistantConfirmPassword"
                    label={copy.confirmAssistantPassword}
                    dependencies={['assistantPassword']}
                    rules={[
                      { required: true, message: copy.confirmAssistantPasswordRequired },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('assistantPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(copy.assistantPasswordsMismatch));
                        }
                      })
                    ]}
                  >
                    <Input.Password size="large" placeholder={copy.confirmAssistantPassword} prefix={<LockOutlined />} />
                  </Form.Item>
                </div>
              </div>

              <Form.Item>
                <Button type="primary" htmlType="submit" block size="large" loading={loading} className="auth-submit">
                  {copy.submit}
                </Button>
              </Form.Item>
            </Form>

            <div className="auth-alt-link">
              <Text>
                {copy.alreadyAccount} <RouterLink to="/login">{copy.login}</RouterLink>
              </Text>
            </div>

            <div className="auth-contact">
              <span>{copy.contact}</span>
              <div className="auth-contact-actions">
                <a href={CONTACT_WHATSAPP_URL} target="_blank" rel="noreferrer">
                  <WhatsAppOutlined /> {copy.whatsapp}
                </a>
                <a href={CONTACT_TEL_URL}>
                  <PhoneOutlined /> {copy.call} {CONTACT_PHONE}
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default RegisterPage;
